<template>
    <div class="properties-panel-content">
        <div class="panel-header">
            <div class="d-flex align-center flex-grow-1" style="min-width: 0;">
                <v-icon class="me-3 flex-shrink-0" :color="node.data.color || 'primary'">{{ node.data.icon || 'mdi-cog'
                    }}</v-icon>
                <v-text-field v-model="node.label" variant="plain" density="compact" hide-details single-line
                    class="font-weight-bold"></v-text-field>
            </div>
            <v-btn icon="mdi-close" color="grey-darken-1" variant="text" density="compact"
                @click="$emit('close')"></v-btn>
        </div>

        <v-divider></v-divider>

        <div class="d-flex flex-grow-0">
            <v-tabs v-model="activeTab" color="primary" grow density="compact">
                <v-tab value="communication" class="text-caption">Communication</v-tab>
                <v-tab value="trigger" class="text-caption">Triggers</v-tab>
                <v-tab v-if="showReviewTab" value="review" class="text-caption">Review</v-tab>
            </v-tabs>
        </div>

        <!-- Shared Keyword Menu -->
        <KeywordAutocomplete ref="autocomplete" :items="keywords" trigger-char="[" @select="handleKeywordSelect" />

        <div class="panel-scroll-area">
            <v-form v-model="formValid" @submit.prevent>
                <v-window v-model="activeTab" class="pa-4">
                    <!-- Communication Tab -->
                    <v-window-item value="communication">
                        <!-- Email Section -->
                        <div class="mb-5">
                            <div class="d-flex align-center mb-2">
                                <v-icon start color="blue" size="small">mdi-email-outline</v-icon>
                                <span class="text-subtitle-2 font-weight-bold">Email Settings</span>
                                <v-spacer></v-spacer>
                                <v-switch v-model="node.data.sendMail" color="success" hide-details density="compact"
                                    inset></v-switch>
                            </div>

                            <v-expand-transition>
                                <div v-if="node.data.sendMail" class="pl-2 border-s-lg border-primary">
                                    <v-text-field v-model="node.data.emailSubject" label="Subject" variant="outlined"
                                        density="compact" prepend-inner-icon="mdi-format-title" class="mb-3"
                                        hide-details="auto" :rules="RULES.REQUIRED_FIELD('Subject')"
                                        ref="emailSubjectRef"
                                        @keyup="$refs.autocomplete.handleKeyUp($event, { field: 'emailSubject', refName: 'emailSubjectRef' })">
                                    </v-text-field>

                                    <v-textarea v-model="node.data.emailBody" label="Message Body" variant="outlined"
                                        density="compact" prepend-inner-icon="mdi-text-box-outline" rows="4" counter
                                        auto-grow :rules="RULES.REQUIRED_FIELD('Message Body')" hide-details="auto"
                                        ref="emailBodyRef"
                                        @keyup="$refs.autocomplete.handleKeyUp($event, { field: 'emailBody', refName: 'emailBodyRef' })">
                                    </v-textarea>
                                </div>
                            </v-expand-transition>
                        </div>

                        <v-divider class="mb-5"></v-divider>

                        <!-- SMS Section -->
                        <div>
                            <div class="d-flex align-center mb-2">
                                <v-icon start color="green" size="small">mdi-message-text-outline</v-icon>
                                <span class="text-subtitle-2 font-weight-bold">SMS Settings</span>
                                <v-spacer></v-spacer>
                                <v-switch v-model="node.data.sendSms" color="success" hide-details density="compact"
                                    inset></v-switch>
                            </div>

                            <v-expand-transition>
                                <div v-if="node.data.sendSms" class="pl-2 border-s-lg border-success">
                                    <v-textarea v-model="node.data.smsBody" label="SMS Content" variant="outlined"
                                        density="compact" prepend-inner-icon="mdi-comment-text-outline" rows="3" counter
                                        auto-grow :rules="RULES.REQUIRED_FIELD('SMS Content')" hide-details="auto"
                                        ref="smsBodyRef"
                                        @keyup="$refs.autocomplete.handleKeyUp($event, { field: 'smsBody', refName: 'smsBodyRef' })">
                                    </v-textarea>
                                </div>
                            </v-expand-transition>
                        </div>
                    </v-window-item>

                    <!-- Trigger Tab -->
                    <v-window-item value="trigger">
                        <v-alert icon="mdi-information" border="start" border-color="primary" class="mb-4 text-caption"
                            density="compact" variant="tonal">
                            Configure execution timing.
                        </v-alert>

          

                        <div v-if="node.data.jobType === WORKFLOW_NODE_TYPES.NOTIFICATION" class="mt-4">
                            <v-select v-model="node.data.triggerEvent"
                                :items="['CREATED', 'CHANGED', 'MISSED', 'CANCELLED']" label="Trigger Event"
                                variant="outlined" prepend-inner-icon="mdi-lightning-bolt" density="compact"
                                hide-details="auto"></v-select>
                            <div class="text-caption text-medium-emphasis mt-2">
                                Sent immediately when the event occurs.
                            </div>
                        </div>

                        <div v-else class="mt-4">
                            <label class="text-caption font-weight-bold mb-2 d-block">Schedule Execution Time</label>
                            <v-row align="center" dense>
                                <v-col cols="5">
                                    <v-text-field v-model.number="node.data.durationValue" type="number"
                                        label="Duration" variant="outlined" density="compact" min="1"
                                        :rules="RULES.REQUIRED_FIELD('Duration')" hide-details
                                        prepend-inner-icon="mdi-timer-sand"></v-text-field>
                                </v-col>
                                <v-col cols="7">
                                    <v-select v-model="node.data.durationUnit" :items="['days', 'hours', 'minutes']"
                                        label="Unit" variant="outlined" density="compact" hide-details></v-select>
                                </v-col>
                            </v-row>
                            <div class="text-caption text-medium-emphasis mt-3">
                                Task executes <strong>{{ node.data.durationValue || 0 }} {{
                                    node.data.durationUnit || 'days' }}</strong> before appointment.
                            </div>
                        </div>
                    </v-window-item>

                    <v-window-item v-if="showReviewTab" value="review">
                        <v-alert icon="mdi-information" border="start" border-color="primary" class="mb-4 text-caption"
                            density="compact" variant="tonal">
                            Configure appointment recall review updates.
                        </v-alert>
                                      <div class="mt-4">
                            <v-switch
                                v-model="node.data.processInsuranceDue"
                                label="Show Review tab"
                                color="primary"
                                inset
                            />
                        </div>
                        <div class="mt-4">
                            <div class="text-subtitle-2 font-weight-bold">
                                Appointment Review Update
                            </div>

                            <v-select
                            v-if="node.data.processInsuranceDue"
                                v-model="selectedRecallTypes"
                                :items="AllRecallTypes"
                                item-title="Description"
                                item-value="RecallTypeNum"
                                label="Recall Types"
                                multiple
                                chips
                                closable-chips
                                clearable
                                density="compact"
                                variant="outlined"
                                :loading="RecallTypesLoading"
                                @update:modelValue="updateProcedures"
                                @update:menu="onRecallMenu"
                            >
                                <template #item="{ item, props }">
                                    <v-list-item v-bind="props">
                                        <v-list-item-subtitle>{{ item.raw.Procedures }}</v-list-item-subtitle>
                                    </v-list-item>
                                </template>
                            </v-select>
                        </div>
                    </v-window-item>
                   <v-window-item v-if="!showReviewTab">
                      <v-alert
  icon="mdi-information"
  border="start"
  border-color="primary"
  class="mb-4 text-caption"
  density="compact"
  variant="tonal"
>
  Insurance due appointments are not configured for 
  {{ node.data.durationValue 
      ? node.data.durationValue + ' day before the appointment' 
      : 'other triggers such as appointment creation or update' }}.
</v-alert>
                   </v-window-item> 
                </v-window>
            </v-form>
        </div>

        <!-- Fixed Bottom Actions -->
        <div class="panel-actions">
            <v-divider class="mb-4"></v-divider>
            <div class="d-flex justify-end px-4 pb-4">
                <v-btn v-if="node.data.job_id" color="primary" variant="text" prepend-icon="mdi-clock-outline"
                    @click="viewJob">View Job</v-btn>
                <v-btn color="error" variant="text" prepend-icon="mdi-delete" @click="deleteNode">Delete Node</v-btn>
            </div>
        </div>
    </div>
</template>

<script>
import { WORKFLOW_NODE_TYPES, REMINDER_TEMPLATES } from '../../taskEngine/constants'
import { RULES } from '../../constants/validationRules'
import KeywordAutocomplete from '../CustomComponents/KeywordAutocomplete.vue'
import ODService from '../../services/odService';
export default {
    name: 'PropertiesPanel',
    props: {
        node: {
            type: Object,
            required: true
        }
    },
    components: {
        KeywordAutocomplete
    },
    emits: ['close', 'delete', 'view-job'],
    data() {
        return {
            activeTab: 'communication',
            formValid: true,
            WORKFLOW_NODE_TYPES,
            RULES,
            keywords: Object.values(REMINDER_TEMPLATES),
            AllRecallTypes:[],
            RecallTypesLoading: false,   
             selectedRecallTypes: []
        }
    },
    computed: {
      showReviewTab() {
        return (
            console.log('Checking showReviewTab condition:', this.node.data.processInsuranceDue, this.node.data?.durationValue, this.node.data?.triggerEvent),
          this.node.data?.durationValue === 1 ||
          ['CREATED', 'CHANGED'].includes(this.node.data?.triggerEvent)
        );
      }
    },
    watch: {
      // When the user switches to a different node, ensure the selected recall types reflect the new node
      'node.id': {
        immediate: true,
        handler() {
          this.selectedRecallTypes = []

          if (!this.node?.data?.processInsuranceDue) return

          if (this.AllRecallTypes.length) {
            this.syncSelectedRecallTypes()
          } else {
            this.getRecallTypes()
          }
        }
      },

      'node.data.processInsuranceDue': {
        immediate: true,
        handler(val) {
          if (val) {
            this.getRecallTypes()
          } else {
            this.node.data.insuranceDueAppointments = []
            this.selectedRecallTypes = []
          }
        }
      },

      // When the underlying procedures list changes (or a new node is loaded), sync the selected recall types.
      'node.data.insuranceDueAppointments': {
        handler() {
          if (this.AllRecallTypes.length) {
            this.syncSelectedRecallTypes()
          }
        }
      }
    },
    methods: {
      syncSelectedRecallTypes() {
        const procedures = this.node?.data?.insuranceDueAppointments || []

        if (!procedures.length) {
          this.selectedRecallTypes = []
          return
        }

        this.selectedRecallTypes = this.AllRecallTypes
          .filter(r =>
            r.ProceduresList?.some(p => procedures.includes(p))
          )
          .map(r => r.RecallTypeNum)
      },

updateProcedures(values){

  const procedures = new Set()

  values.forEach(typeNum=>{
    const recall = this.AllRecallTypes.find(r => r.RecallTypeNum === typeNum)

    if(recall){
      recall.ProceduresList.forEach(p => procedures.add(p))
    }
  })

  this.node.data.insuranceDueAppointments = [...procedures]

},
            async onRecallMenu(isOpen) {
    if (!isOpen) return

    if (this.AllRecallTypes.length > 0) return

    await this.getRecallTypes()
    },
async getRecallTypes() {
  if (this.RecallTypesLoading) return
  this.RecallTypesLoading = true

  try {
    const res = await ODService.getODrecalltypes()

    if (res.status >= 400) {
      console.error("API error:", res.status)
      return
    }

    this.AllRecallTypes = res.data.data.map(r => ({
      ...r,
      ProceduresList: r.Procedures ? r.Procedures.split(',') : []
    }))

    // preselect recall types from existing procedures
    if (this.node.data.insuranceDueAppointments?.length) {

      this.selectedRecallTypes = this.AllRecallTypes
        .filter(r =>
          r.ProceduresList.some(p =>
            this.node.data.insuranceDueAppointments.includes(p)
          )
        )
        .map(r => r.RecallTypeNum)
    }
  } catch (err) {
    console.error("Error fetching recall types:", err)
  } finally {
    this.RecallTypesLoading = false
  }
},
        deleteNode() {
            this.$emit('delete', this.node.id)
        },
        viewJob() {
            this.$emit('view-job', this.node.data.job_id)
        },
        handleKeywordSelect({ item, context, range }) {
            if (!context) return;
            const { field, refName } = context;

            // Update data model
            const currentVal = this.node.data[field] || '';

            // Try to insert at cursor position
            const component = this.$refs[refName];
            const element = component ? component.$el.querySelector('input, textarea') : null;

            if (element) {
                let start, end;

                if (range) {
                    start = range.start;
                    end = range.end;
                } else {
                    start = element.selectionStart;
                    end = element.selectionEnd;

                    // Fallback: check if previous char was '[' to replace it
                    if (start > 0 && currentVal[start - 1] === '[') {
                        start = start - 1;
                    }
                }

                // Insert text
                const newVal = currentVal.substring(0, start) + item + currentVal.substring(end);
                this.node.data[field] = newVal;

                // Restore focus and cursor
                this.$nextTick(() => {
                    element.focus();
                    const newCursorPos = start + item.length;
                    element.setSelectionRange(newCursorPos, newCursorPos);
                });
            } else {
                // Fallback: append
                this.node.data[field] = currentVal + item;
            }
        }
    }
}
</script>

<style scoped>
.properties-panel-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: white;
    overflow: hidden;
}

.panel-header {
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fafafa;
    flex-shrink: 0;
}

.panel-scroll-area {
    flex-grow: 1;
    overflow-y: auto;
    min-height: 0;
}

.panel-actions {
    flex-shrink: 0;
    background: white;
}

.border-s-lg {
    border-left-width: 3px !important;
    border-left-style: solid;
}
</style>
